import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  HourglassIcon, SpinnerGapIcon, GlobeIcon, XLogoIcon, TelegramLogoIcon,
  GithubLogoIcon, AlarmIcon, TrashIcon, BellRingingIcon, TrophyIcon, SlidersIcon,
} from "@phosphor-icons/react";
import { fetchWaitingRoom, dismissWaiter, saveWaiterSettings } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TradeButton } from "../components/TradeButton";
import { FollowBuilderButton } from "../components/FollowBuilderButton";
import { useSeo } from "../lib/useSeo";
import { fmtUsd } from "../lib/format";

const GH_COLORS = { building: "#00E599", active: "#F0C000" };

const InfoBadges = ({ d }) => (
  <span className="flex items-center gap-1.5">
    {d.hasWebsite && <GlobeIcon size={13} className="text-neon" title="Website" />}
    {d.hasTwitter && <XLogoIcon size={13} className="text-white" title="X / Twitter" />}
    {d.hasTelegram && <TelegramLogoIcon size={13} className="text-[#2AABEE]" title="Telegram" />}
    {d.github && (
      <a
        href={d.github} target="_blank" rel="noreferrer"
        title={d.ghVerdict
          ? `GitHub: ${d.ghVerdict}${d.ghDaysSincePush != null ? ` · pushed ${d.ghDaysSincePush}d ago` : ""}${d.ghStars ? ` · ★${d.ghStars}` : ""}${d.ghLanguage ? ` · ${d.ghLanguage}` : ""}`
          : "GitHub repo linked — activity check pending"}
      >
        <GithubLogoIcon size={14} weight="fill" style={{ color: GH_COLORS[d.ghVerdict] || "#8A8F98" }} />
      </a>
    )}
  </span>
);

const ageFmt = (h) => (h == null ? "—" : h < 48 ? `${Math.round(h)}h` : `${Math.round(h / 24)}d`);

// Tiny volume sparkline — see a waiter stirring before it officially wakes.
const VolSpark = ({ hist, stirring }) => {
  if (!hist || hist.length < 2) return <span className="text-[10px] font-mono text-muted-foreground">—</span>;
  const max = Math.max(...hist, 1);
  return (
    <span className="flex items-end gap-[2px] h-5" title={`h1 volume, last ${hist.length} checks (~5 min apart)`}>
      {hist.map((v, i) => (
        <span key={i} className={stirring ? "w-[4px] bg-[#F0C000]/80" : "w-[4px] bg-neon/50"}
          style={{ height: `${Math.max(8, (v / max) * 100)}%` }} />
      ))}
    </span>
  );
};

const TUNE_FIELDS = [
  { key: "minMcap", label: "Min MCap $" }, { key: "maxMcap", label: "Max MCap $" },
  { key: "minLiq", label: "Min Liq $" }, { key: "wakePct", label: "Wake +% (1h)" },
  { key: "wakeVolX", label: "Wake Vol x" }, { key: "entryPingMin", label: "Entry ping ≥ score" },
];

const TuningPanel = ({ criteria, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const f = form || criteria || {};

  const save = async () => {
    setBusy(true);
    try {
      const res = await saveWaiterSettings({
        minMcap: +f.minMcap, maxMcap: +f.maxMcap, minLiq: +f.minLiq, wakePct: +f.wakePct, wakeVolX: +f.wakeVolX,
        entryPingMin: +f.entryPingMin,
      });
      toast.success("Room tuned — the scanner uses the new thresholds on its next pass");
      setForm(null); setOpen(false); onSaved(res.criteria);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save — admin login required"); }
    setBusy(false);
  };

  return (
    <div className="border border-white/10 bg-card" data-testid="waiter-tuning-panel">
      <button data-testid="waiter-tuning-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-muted-foreground hover:text-white transition-colors">
        <SlidersIcon size={15} className="text-neon" /> Room tuning (admin) — mcap band & wake sensitivity, no redeploy
        <span className="ml-auto font-mono text-[10px]">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {TUNE_FIELDS.map((t) => (
              <label key={t.key} className="space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground">{t.label}</span>
                <input data-testid={`waiter-tune-${t.key}`} type="number" min="0" step="any"
                  value={f[t.key] ?? ""}
                  onChange={(e) => setForm((s) => ({ ...(s || criteria), [t.key]: e.target.value }))}
                  className="w-full bg-black/40 border border-white/15 px-2.5 py-2 text-sm font-mono focus:border-neon/60 outline-none" />
              </label>
            ))}
          </div>
          <button data-testid="waiter-tune-save" onClick={save} disabled={busy || !form}
            className="px-5 py-2 bg-neon text-black text-xs font-bold hover:bg-neon/90 transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Save thresholds"}
          </button>
        </div>
      )}
    </div>
  );
};

export default function WaitingRoom() {
  useSeo({ title: "Waiting Room — Quiet Coins With Good Bones | AlphaApeing", description: "Young 10-50K mcap Solana coins with real token info that haven't run yet — get pinged the moment one wakes up." });
  const qc = useQueryClient();
  const { user } = useAuth();
  const q = useQuery({ queryKey: ["waitingRoom"], queryFn: fetchWaitingRoom, refetchInterval: 60000 });

  const dismiss = async (mint, sym) => {
    try {
      await dismissWaiter(mint);
      qc.invalidateQueries({ queryKey: ["waitingRoom"] });
      toast.success(`$${(sym || "?").toUpperCase()} removed from the Waiting Room`);
    } catch { toast.error("Failed to dismiss"); }
  };

  const waiting = q.data?.waiting || [];
  const woke = q.data?.woke || [];
  const stats = q.data?.stats || {};
  const crit = q.data?.criteria || {};
  const outcomes = q.data?.outcomes || {};

  return (
    <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="waiting-room-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black"><HourglassIcon size={24} weight="fill" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">WAITING <span className="text-neon">ROOM</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm max-w-3xl">
          Quiet coins with good bones: {fmtUsd(crit.minMcap || 10000)}–{fmtUsd(crit.maxMcap || 50000)} mcap, under 7 days old, real token info
          (website / socials / GitHub), and they <span className="text-white">haven't run yet</span>. You get a 🚪 Telegram ping when a strong one
          enters (score ≥ {crit.entryPingMin ?? 50}), 👀 when it starts stirring here, and the ⏰ wake-up ping (+{crit.wakePct || 30}% in 1h, {crit.wakeVolX || 3}x volume)
          auto-adds it to your Watchlist. Manage pings on the <Link to="/alerts" className="text-neon hover:underline inline-flex items-center gap-1"><BellRingingIcon size={12} />Alerts page</Link>.
        </p>
        <div className="flex gap-4 mt-3 font-mono text-xs text-muted-foreground flex-wrap" data-testid="waiting-room-stats">
          <span>😴 waiting: <span className="text-white font-bold">{stats.waiting ?? "—"}</span></span>
          <span>⏰ woke (24h): <span className="text-neon font-bold">{stats.woke24h ?? "—"}</span></span>
          <span>💀 died (24h): <span className="text-danger font-bold">{stats.dead24h ?? "—"}</span></span>
        </div>
      </div>

      {(outcomes.tracked || 0) > 0 && (
        <div className="border border-[#F0C000]/30 bg-[#F0C000]/[0.04] p-4 flex flex-wrap items-center gap-x-6 gap-y-1 font-mono text-xs" data-testid="waiter-outcomes">
          <span className="flex items-center gap-1.5 text-[#F0C000] font-bold"><TrophyIcon size={15} weight="fill" /> ROOM RECORD</span>
          <span className="text-muted-foreground">{outcomes.tracked} wake-up{outcomes.tracked > 1 ? "s" : ""} tracked</span>
          <span className="text-muted-foreground"><span className="text-neon font-bold">{outcomes.winners2x}</span> hit 2x+ after waking</span>
          {outcomes.avgX != null && <span className="text-muted-foreground">avg peak <span className="text-white font-bold">{outcomes.avgX}x</span></span>}
          {outcomes.bestX != null && <span className="text-muted-foreground">best <span className="text-neon font-bold">{outcomes.bestX}x</span></span>}
        </div>
      )}

      {user?.role === "admin" && <TuningPanel criteria={crit} onSaved={() => qc.invalidateQueries({ queryKey: ["waitingRoom"] })} />}

      {woke.length > 0 && (
        <div className="space-y-2" data-testid="woke-section">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><AlarmIcon size={18} weight="fill" className="text-neon" /> Recent wake-ups</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {woke.map((d) => (
              <div key={d.mint} className="border border-neon/40 bg-neon/[0.04] p-4" data-testid={`woke-card-${d.mint}`}>
                <div className="flex items-center gap-2">
                  {d.image && <img src={d.image} alt="" className="w-7 h-7 object-cover border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />}
                  <Link to={`/coin/${d.mint}`} className="font-heading font-bold hover:text-neon">${(d.symbol || "?").toUpperCase()}</Link>
                  <span className="ml-auto font-mono text-xs text-neon font-bold">+{Math.round(d.wokeStats?.chg1h || 0)}% /1h</span>
                </div>
                <div className="font-mono text-[11px] text-muted-foreground mt-2">
                  waited {d.wokeStats?.waitedH ?? d.waitedH}h · woke at {fmtUsd(d.wokeStats?.mcap)} MC
                  {d.wokeStats?.volX ? ` · vol ${d.wokeStats.volX}x` : ""}
                </div>
                {(d.outcomeX != null || d.sinceWakePct != null) && (
                  <div className="font-mono text-[11px] mt-1" data-testid={`woke-outcome-${d.mint}`}>
                    {d.outcomeX != null && <span className={d.outcomeX >= 2 ? "text-neon font-bold" : "text-[#F0C000]"}>peak {d.outcomeX}x since wake</span>}
                    {d.sinceWakePct != null && <span className={`ml-2 ${d.sinceWakePct >= 0 ? "text-neon" : "text-[#FF3366]"}`}>now {d.sinceWakePct >= 0 ? "+" : ""}{Math.round(d.sinceWakePct)}%</span>}
                  </div>
                )}
                <div className="mt-2"><TradeButton coin={{ address: d.mint, symbol: d.symbol, url: d.url }} size="sm" /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="py-16 flex justify-center"><SpinnerGapIcon size={28} className="animate-spin text-neon" /></div>
      ) : waiting.length === 0 ? (
        <div className="border border-white/10 bg-card py-16 text-center text-muted-foreground font-mono text-sm px-6" data-testid="waiting-room-empty">
          The room is empty — the sweeper runs every 10 minutes and only admits young coins with real token info that haven't moved yet. Check back soon.
        </div>
      ) : (
        <div className="border border-white/10 bg-card overflow-x-auto" data-testid="waiting-room-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase text-muted-foreground border-b border-white/10">
                <th className="py-2.5 px-3">Coin</th><th className="py-2.5 px-3">Waiter score</th>
                <th className="py-2.5 px-3">MCap</th><th className="py-2.5 px-3">Peak</th><th className="py-2.5 px-3">Liq</th>
                <th className="py-2.5 px-3">1h %</th><th className="py-2.5 px-3">Vol pulse</th><th className="py-2.5 px-3">Age / waited</th>
                <th className="py-2.5 px-3">Info</th><th className="py-2.5 px-3">Dev</th><th className="py-2.5 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {waiting.map((d) => (
                <tr key={d.mint} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`waiter-row-${d.mint}`}>
                  <td className="py-2 px-3">
                    <Link to={`/coin/${d.mint}`} className="flex items-center gap-2 hover:text-neon">
                      {d.image && <img src={d.image} alt="" className="w-6 h-6 object-cover border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />}
                      <span className="font-bold">${(d.symbol || "?").toUpperCase()}</span>
                      {d.stirring && (
                        <span data-testid={`waiter-stirring-${d.mint}`} title="Stirring — volume 1.5x+ its sleepy average and price ticking up. Approaching wake territory."
                          className="text-[9px] font-mono px-1 py-0.5 border bg-[#F0C000]/10 border-[#F0C000]/40 text-[#F0C000] whitespace-nowrap animate-pulse">👀 STIRRING</span>
                      )}
                      {d.t3Score >= 30 && (
                        <span data-testid={`waiter-t3-${d.mint}`} title={`Tier 3 Confirmation ${d.t3Score}/100 — ${d.t3Label}. Boosting this waiter's score.`}
                          className="text-[9px] font-mono px-1 py-0.5 border bg-neon/10 border-neon/40 text-neon whitespace-nowrap">🎯 T3 {d.t3Score}</span>
                      )}
                      {d.kolPosted > 0 && (
                        <span data-testid={`waiter-kol-${d.mint}`} title={`${d.kolPosted} tracked KOL(s) posted this CA — big signal for a quiet coin (+8 score)`}
                          className="text-[9px] font-mono px-1 py-0.5 border bg-[#F0C000]/10 border-[#F0C000]/40 text-[#F0C000] whitespace-nowrap">👑 KOL</span>
                      )}
                    </Link>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2" title={d.baseScore && d.waiterScore > d.baseScore ? `Base ${d.baseScore} + ${d.waiterScore - d.baseScore} Tier 3/KOL boost` : undefined}>
                      <div className="w-14 h-1.5 bg-white/5"><div className="h-full bg-neon/70" style={{ width: `${d.waiterScore || 0}%` }} /></div>
                      <span className="font-mono text-xs">{d.waiterScore}{d.baseScore && d.waiterScore > d.baseScore ? <span className="text-neon">↑</span> : null}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">{fmtUsd(d.lastMcap)}</td>
                  <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{fmtUsd(d.peakMcap)}</td>
                  <td className="py-2 px-3 font-mono text-xs">{fmtUsd(d.liquidity)}</td>
                  <td className={`py-2 px-3 font-mono text-xs ${(d.chg1h || 0) >= 0 ? "text-neon" : "text-[#FF3366]"}`}>{d.chg1h != null ? `${Math.round(d.chg1h)}%` : "—"}</td>
                  <td className="py-2 px-3"><VolSpark hist={d.volHistory} stirring={d.stirring} /></td>
                  <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{ageFmt(d.ageHoursAtEntry)} / {ageFmt(d.waitedH)}</td>
                  <td className="py-2 px-3"><InfoBadges d={d} /></td>
                  <td className="py-2 px-3 font-mono text-[11px] text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <span>
                        {d.devStats?.runnerCount ? <span className="text-neon">🎯 {d.devStats.runnerCount} runner{d.devStats.runnerCount > 1 ? "s" : ""}</span> : d.devStats?.totalTokens ? `${d.devStats.totalTokens} tokens` : "—"}
                      </span>
                      {d.creator && <FollowBuilderButton creator={d.creator} size="sm" labels={["Track dev", "Tracking"]} />}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1.5">
                      <TradeButton coin={{ address: d.mint, symbol: d.symbol, url: d.url }} size="sm" />
                      <button data-testid={`dismiss-waiter-${d.mint}`} onClick={() => dismiss(d.mint, d.symbol)} title="Not interested — remove"
                        className="p-1.5 text-muted-foreground hover:text-danger transition-colors"><TrashIcon size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
